/* SAVIO Sections — shared behaviour for savio-* sections: video source swap,
   lazy/viewport playback, play/pause toggle, and mobile sticky add-to-cart.
   Reuses the existing window.SavioCart.addItems() API from savio_cart_drawer.liquid —
   no parallel cart mechanism. */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var desktopQuery = window.matchMedia('(min-width: 750px)');

  function activeVideoFor(root) {
    if (reducedMotion) return null;
    var wantDesktop = desktopQuery.matches;
    var desktop = root.querySelector('[data-savio-video="desktop"]');
    var mobile = root.querySelector('[data-savio-video="mobile"]');
    var chosen = wantDesktop ? (desktop || mobile) : (mobile || desktop);
    var other = chosen === desktop ? mobile : desktop;
    return { chosen: chosen, other: other };
  }

  function ensureLoaded(video) {
    if (!video || video.dataset.savioLoaded) return;
    video.dataset.savioLoaded = 'true';
    video.setAttribute('preload', 'auto');
    if (!video.getAttribute('src') && video.querySelector('source')) {
      video.load();
    }
  }

  function unload(video) {
    if (!video || !video.dataset.savioLoaded) return;
    video.pause();
  }

  function playVideo(video) {
    if (!video) return;
    ensureLoaded(video);
    var attempt = video.play();
    if (attempt && attempt.catch) {
      attempt.then(function () {
        video.classList.add('is-active');
      }).catch(function () {
        /* Autoplay blocked — poster stays visible, no error surfaced to visitor. */
      });
    } else {
      video.classList.add('is-active');
    }
  }

  function pauseVideo(video) {
    if (!video) return;
    video.classList.remove('is-active');
    video.pause();
  }

  function setupMedia(root) {
    var autoplay = root.dataset.savioAutoplay !== 'false';
    var priority = root.dataset.savioVideoPriority || 'low';
    var state = { userPaused: false };

    function refreshActive() {
      var pair = activeVideoFor(root);
      if (!pair) return;
      if (pair.other) pauseVideo(pair.other);
      if (pair.chosen && autoplay && !state.userPaused && root.dataset.savioInView !== 'false') {
        playVideo(pair.chosen);
      }
      return pair.chosen;
    }

    desktopQuery.addEventListener
      ? desktopQuery.addEventListener('change', refreshActive)
      : desktopQuery.addListener(refreshActive);

    var toggle = root.querySelector('[data-savio-media-toggle]');
    if (toggle) {
      toggle.addEventListener('click', function () {
        var pair = activeVideoFor(root);
        var video = pair && pair.chosen;
        if (!video) return;
        if (video.classList.contains('is-active') && !video.paused) {
          pauseVideo(video);
          state.userPaused = true;
          toggle.setAttribute('aria-pressed', 'false');
          toggle.setAttribute('aria-label', 'Play video');
        } else {
          state.userPaused = false;
          playVideo(video);
          toggle.setAttribute('aria-pressed', 'true');
          toggle.setAttribute('aria-label', 'Pause video');
        }
      });
    }

    if (priority === 'high') {
      refreshActive();
      return;
    }

    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          root.dataset.savioInView = entry.isIntersecting ? 'true' : 'false';
          if (entry.isIntersecting) {
            refreshActive();
          } else {
            var pair = activeVideoFor(root);
            if (pair) { pauseVideo(pair.chosen); }
          }
        });
      }, { rootMargin: '200px 0px', threshold: 0.1 });
      io.observe(root);
    } else {
      refreshActive();
    }
  }

  function initMedia() {
    document.querySelectorAll('[data-savio-media]').forEach(setupMedia);
  }

  /* ---------------- Sticky add-to-cart ---------------- */
  function initStickyAtc(bar) {
    var afterEl = bar.dataset.showAfter ? document.querySelector(bar.dataset.showAfter) : null;
    var hideWhenVisibleEl = bar.dataset.hideWhenVisible ? document.querySelector(bar.dataset.hideWhenVisible) : null;
    var scrollThreshold = parseInt(bar.dataset.showAfterScroll || '600', 10);

    function evaluate() {
      var pastThreshold = afterEl
        ? afterEl.getBoundingClientRect().bottom < 0
        : window.scrollY > scrollThreshold;
      var nativeVisible = false;
      if (hideWhenVisibleEl) {
        var rect = hideWhenVisibleEl.getBoundingClientRect();
        nativeVisible = rect.top < window.innerHeight && rect.bottom > 0;
      }
      bar.classList.toggle('is-visible', pastThreshold && !nativeVisible);
    }

    window.addEventListener('scroll', evaluate, { passive: true });
    window.addEventListener('resize', evaluate);
    evaluate();

    var btn = bar.querySelector('[data-savio-sticky-add]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var variantId = btn.dataset.variantId;
      if (!variantId) return;
      btn.disabled = true;
      var original = btn.textContent;
      btn.textContent = 'Adding…';

      var finish = function () {
        btn.disabled = false;
        btn.textContent = original;
      };

      if (window.SavioCart && typeof window.SavioCart.addItems === 'function') {
        window.SavioCart.addItems([{ id: Number(variantId), quantity: 1 }])
          .then(finish)
          .catch(function () {
            finish();
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart';
          });
      } else {
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
        })
          .then(function () { window.location.href = '/cart'; })
          .catch(finish);
      }
    });
  }

  function initStickyBars() {
    document.querySelectorAll('[data-savio-sticky-atc]').forEach(initStickyAtc);
  }

  /* ---------------- Featured product add-to-cart ---------------- */
  function initFeaturedAtc() {
    document.querySelectorAll('[data-savio-featured-atc]').forEach(function (form) {
      var qtyInput = form.querySelector('[data-savio-qty]');
      var minus = form.querySelector('[data-savio-qty-minus]');
      var plus = form.querySelector('[data-savio-qty-plus]');
      if (minus && qtyInput) {
        minus.addEventListener('click', function () {
          qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
        });
      }
      if (plus && qtyInput) {
        plus.addEventListener('click', function () {
          qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
        });
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var select = form.querySelector('[data-savio-variant-id]');
        var variantId = select ? select.value : form.dataset.variantId;
        var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
        if (!variantId) return;
        var btn = form.querySelector('button[type="submit"]');
        var original = btn ? btn.textContent : null;
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }

        var reset = function () {
          if (btn) { btn.disabled = false; btn.textContent = original; }
        };

        if (window.SavioCart && typeof window.SavioCart.addItems === 'function') {
          window.SavioCart.addItems([{ id: Number(variantId), quantity: quantity }]).then(reset).catch(function () {
            reset();
            window.location.href = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root || '/') + 'cart';
          });
        } else {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: quantity }] })
          }).then(function () { window.location.href = '/cart'; }).catch(reset);
        }
      });
    });
  }

  /* ---------------- Product grid quick add ---------------- */
  function initQuickAdd() {
    document.querySelectorAll('[data-savio-quickadd]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.dataset.variantId;
        if (!variantId) return;
        btn.disabled = true;
        var original = btn.textContent;
        btn.textContent = 'Adding…';
        var reset = function () { btn.disabled = false; btn.textContent = original; };

        if (window.SavioCart && typeof window.SavioCart.addItems === 'function') {
          window.SavioCart.addItems([{ id: Number(variantId), quantity: 1 }]).then(reset).catch(reset);
        } else {
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
          }).then(function () { window.location.href = '/cart'; }).catch(reset);
        }
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initMedia();
      initStickyBars();
      initFeaturedAtc();
      initQuickAdd();
    });
  } else {
    initMedia();
    initStickyBars();
    initFeaturedAtc();
    initQuickAdd();
  }
})();
